import {
	formatPrice,
	isNative,
	isWeb,
	useCancelReservationMutation,
	useEstimateProcessingFeeQuery,
	useEstimateSubscriptionProcessingFeeQuery,
	useV2CancelSubscriptionMutation,
} from "@stepapp/core";
import { captureSentryException } from "@stepapp/sentry";
import {
	Block,
	Button,
	palette,
	spacing,
	Touchable,
	Typography,
} from "@stepapp/ui";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Platform, ScrollView, StyleSheet } from "react-native";
import { ErrorModal } from "../../../src/components";
import CancelSubscriptionPromoCodeSheet from "../../../src/components/Modal/CancelSubscriptionPromoCodeSheet";
import EditReservationLoading from "../../../src/screens/EditReservation/EditReservationLoading";
import EditReservationSuccess from "../../../src/screens/EditReservation/EditReservationSuccess";
import { AppStorage } from "../../../src/services/AppStorage";
import { smallPageWrapper } from "../../../src/styles/smallPageWrapper";
import { footerStyle } from "../../../src/styles/themes";

type StatusType = "success" | "loading" | "error" | "";

const subscriptionCancelReasons = [
	"screens.cancelReservation.cancelCause.movingToANewPlace",
	"screens.cancelReservation.cancelCause.serviceNotGoodEnough",
	"screens.cancelReservation.cancelCause.haveBetterOffer",
	"screens.cancelReservation.cancelCause.problemWithWorker",
	"screens.cancelReservation.cancelCause.other",
] as const;

const reservationCancelReasons = [
	"screens.cancelReservation.cancelCause.noLongerNeedTheService",
	"screens.cancelReservation.cancelCause.haveBetterOffer",
	"screens.cancelReservation.cancelCause.problemWithPlatform",
	"screens.cancelReservation.cancelCause.other",
] as const;

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TODO
const CancelReservation = () => {
	const { t } = useTranslation();
	const { back, replace } = useRouter();
	const priceFormatter = formatPrice(AppStorage().get("countryCode"));
	const [status, setStatus] = useState<StatusType>("");
	const [showPromoCode, setShowPromoCode] = useState(false);
	const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);

	const [cancelReservation] = useCancelReservationMutation();
	const [cancelSubscription] = useV2CancelSubscriptionMutation();

	const [cancelReason, setCancelReason] = useState<
		| (typeof subscriptionCancelReasons)[number]
		| (typeof reservationCancelReasons)[number]
		| undefined
	>();
	const { reservationId = "", subscriptionId = "" } = useLocalSearchParams<{
		reservationId?: string;
		subscriptionId?: string;
	}>();
	const isSubscription = !!subscriptionId;

	const fallbackErrorMsg = isSubscription
		? "alerts.cantCancelSubscription"
		: "alerts.cantCancelReservation";

	const { data: processingFee } = useEstimateProcessingFeeQuery({
		variables: { reservationId },
		skip: isSubscription,
	});
	const { data: subscruptionProcessingFee } =
		useEstimateSubscriptionProcessingFeeQuery({
			variables: { subscriptionId },
			skip: !subscriptionId,
		});

	const reservationCancellationFee = processingFee?.estimateProcessingFee
		?.amount
		? priceFormatter(processingFee?.estimateProcessingFee?.amount, {
				currency: processingFee?.estimateProcessingFee?.currency,
			})
		: "";

	const subscriptionCancellationFee = subscruptionProcessingFee
		?.estimateSubscriptionProcessingFee?.amount
		? priceFormatter(
				subscruptionProcessingFee?.estimateSubscriptionProcessingFee?.amount,
				{
					currency:
						subscruptionProcessingFee?.estimateSubscriptionProcessingFee
							?.currency,
				},
			)
		: "";

	const cancellationFee =
		reservationCancellationFee || subscriptionCancellationFee;
	const canUsePromoCode =
		!!subscruptionProcessingFee?.estimateSubscriptionProcessingFee?.promoCode &&
		!subscruptionProcessingFee?.estimateSubscriptionProcessingFee
			?.promoCodeAlreadyUsed;

	const handleGoBack = useCallback(() => {
		back();
	}, [back]);

	const handleCancelReservationOrSubscription = useCallback(async () => {
		setStatus("loading");

		const reason = cancelReason ? t(cancelReason) : "";

		const request = isSubscription
			? cancelSubscription({
					variables: { reason, subscriptionId },
					refetchQueries: ["GetReservations"],
				})
			: cancelReservation({
					variables: { reservationId, reason },
					refetchQueries: ["GetReservations"],
				});

		const { errors } = await request;

		if (errors) {
			setStatus("");

			captureSentryException(
				`[Cancel reservation error]: cancel ${isSubscription ? "subscription" : "reservation"} error`,
				{
					extra: {
						subscriptionId,
						reservationId,
						cancelReason: reason,
						errorMessage: errors?.[0]?.message,
					},
				},
			);

			return setErrorMsg(errors?.[0]?.message);
		}

		setStatus("success");
	}, [
		isSubscription,
		reservationId,
		cancelReason,
		subscriptionId,
		t,
		cancelSubscription,
		cancelReservation,
	]);

	const handleCancel = useCallback(() => {
		if (canUsePromoCode) {
			return setShowPromoCode(true);
		}

		handleCancelReservationOrSubscription();
	}, [canUsePromoCode, handleCancelReservationOrSubscription]);

	const handleCloseSuccessEdit = useCallback(() => {
		replace("/calendar");
	}, [replace]);

	if (status === "loading") {
		return <EditReservationLoading />;
	}

	if (status === "success") {
		return (
			<EditReservationSuccess
				questionType={
					isSubscription ? "cancelSubscription" : "cancelReservation"
				}
				handleClose={handleCloseSuccessEdit}
			/>
		);
	}

	return (
		<>
			<ScrollView
				contentContainerStyle={[styles.container, isWeb && smallPageWrapper]}
			>
				<Typography type="primary" variant="largeTitle">
					{t(
						isSubscription
							? "screens.cancelReservation.whyCancelSubscription"
							: "screens.cancelReservation.whyCancelReservation",
					)}
				</Typography>
				<Block marginTop={1} marginBottom={1.5}>
					<Typography type="secondary" variant="paragraph">
						{t(
							isSubscription
								? "screens.cancelReservation.cancelAllScheduledAppointments"
								: "screens.cancelReservation.weWantToImprove",
						)}
					</Typography>
				</Block>

				{Object.values(
					isSubscription ? subscriptionCancelReasons : reservationCancelReasons,
				)?.map((value) => {
					const isSelected = cancelReason === value;
					return (
						<Touchable onPress={() => setCancelReason(value)} key={value}>
							<Block
								marginTop={1.5}
								justifyCenter
								paddingHorizontal={1.5}
								style={[
									styles.content,
									isSelected &&
										(isNative ? styles.selectedWithoutShadow : styles.selected),
								]}
							>
								<Typography
									type={isSelected ? "white" : "primary"}
									variant="button"
								>
									{t(value)}
								</Typography>
							</Block>
						</Touchable>
					);
				})}
			</ScrollView>

			<Block style={[footerStyle, styles.footer]}>
				<Button
					disabled={!cancelReason}
					label={t(
						isSubscription
							? "screens.cancelReservation.confirmCancelSubscription"
							: "screens.cancelReservation.confirmCancelReservation",
						{
							fee: cancellationFee && `(${cancellationFee})`,
						},
					)}
					onPress={handleCancel}
					type="primary"
				/>
				<Button
					label={t(
						isSubscription
							? "screens.cancelReservation.dontCancelSubscription"
							: "screens.cancelReservation.dontCancelAppointment",
					)}
					onPress={handleGoBack}
					type="secondary"
					style={styles.goBackButton}
				/>
			</Block>

			<CancelSubscriptionPromoCodeSheet
				handleCancelSubscription={handleCancelReservationOrSubscription}
				toggle={() => setShowPromoCode(false)}
				isVisible={showPromoCode}
				promoCode={
					subscruptionProcessingFee?.estimateSubscriptionProcessingFee
						?.promoCode
				}
				subscriptionCancellationFee={subscriptionCancellationFee}
				newAddressDiscount={cancelReason === subscriptionCancelReasons[0]}
			/>

			<ErrorModal
				errorMsg={errorMsg}
				setErrorMsg={setErrorMsg}
				fallbackErrorMsg={fallbackErrorMsg}
			/>
		</>
	);
};

export default CancelReservation;

const styles = StyleSheet.create({
	container: {
		marginTop: spacing.unit * 2,
		paddingBottom: spacing.unit * 25,
	},
	goBackButton: {
		...Platform.select({
			default: {},
			web: { marginTop: spacing.unit * 2 },
		}),
	},
	footer: {
		paddingHorizontal: spacing.unit * 2,
		paddingTop: spacing.unit * 2,
	},
	content: {
		backgroundColor: palette.common.lightGray,
		borderRadius: spacing.radius,
		minHeight: spacing.unit * 9,
		overflow: "hidden",
	},
	selected: {
		backgroundColor: palette.primary.accent,
		boxShadow:
			"0px 100px 80px rgba(0, 0, 84, 0.07), 0px 41.7776px 33.4221px rgba(0, 0, 84, 0.0503198), 0px 22.3363px 17.869px rgba(0, 0, 84, 0.0417275), 0px 12.5216px 10.0172px rgba(0, 0, 84, 0.035), 0px 6.6501px 5.32008px rgba(0, 0, 84, 0.0282725), 0px 2.76726px 2.21381px rgba(0, 0, 84, 0.0196802)",
	},
	selectedWithoutShadow: {
		backgroundColor: palette.primary.accent,
	},
});
